/**
 * Download — the legacy's export tab, rebuilt (ADR-0021 flow parity).
 *
 * The legacy ends its flow here: pick a return, take the Excel or the JSON.
 * Its preamble reads "Every download reflects all your latest edits", and
 * nothing is gated at export time.
 *
 * **Ours is gated, and that difference is the point.** An artifact here is a
 * generated, versioned, hash-bound object produced at a lifecycle transition —
 * not a live render of whatever the screen happens to hold. So this tab lists
 * what was generated, says which state generated it, and shows the SHA the
 * approval loop bound. If a period has produced nothing yet, the honest answer
 * is that there is nothing to download, not a freshly-rendered file that no
 * one approved.
 *
 * A `current` artifact is the one in force; `superseded` rows stay visible
 * because an approval may have been given against one of them, and hiding it
 * would erase the trail.
 *
 * The URL is minted per click and short-lived (`presign_read`, which logs the
 * read). It is a capability, so it is never stored, never rendered into an
 * href on load, and never reused.
 */

import { Alert, Button } from '@fiveox/ui'
import { Download as DownloadIcon, FileJson, FileSpreadsheet, FileText } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, type PeriodGates, type PeriodRecord, periods } from '../lib/api'
import { saveFile } from '../lib/files'
import { periodWords } from '../lib/periodWords'
import {
  artifactStatusWords,
  KIND_LABEL,
  RETURN_NAMES,
  STATE_LABELS,
  returnName,
} from '../lib/returnWords'
import { useClient } from './ClientLayout'


const KIND_HINT: Record<string, string> = {
  client_workbook: 'What the client sees and approves',
  offline_utility: 'The government offline-utility template, filled',
  gstn_json: 'The portal payload, at the portal grain',
  // The two the table has admitted for weeks and this list did not. Both are
  // written in production, so both reached this screen — as a slug with a
  // blank hint beside it.
  filed_return: 'What the portal returned — the return as actually filed',
  gstr3b_working: 'The working paper: three ITC lines, and which one is claimed',
  // W3-12 (5b), D-53: the practice's own working sheet, in its own layout.
  working_sheet: 'The working sheet: computation, return view, HSN, register, voucher lists',
  // B-07 (FR-E13): the 1A's own payload, beside the month's GSTR-1 JSON.
  gstr1a_json: 'The GSTR-1A payload — only what changed since the GSTR-1 was filed',
  // e2e-filing B-06: the amendment tables built against an earlier filed month, kept so
  // the utility workbook and the JSON of this month write them.
  amendments: 'The amendment tables against an earlier filed month, as the outputs write them',
  // C-09 / C-08 (FR-E24, FR-E23): the GSTR-3B's filing outputs, whole rupees, one set of figures.
  gstr3b_json: 'The GSTR-3B payload, written when the client approves',
  gstr3b_return: 'The GSTR-3B return sheet: every cell as filed — GSTN’s 3B utility is still owed',
  gstr3b_challan: 'The challan working: cash to pay per head after set-off',
}

function KindIcon({ kind }: { kind: string }) {
  if (kind === 'gstn_json' || kind === 'gstr1a_json' || kind === 'gstr3b_json')
    return <FileJson size={16} />
  if (
    kind === 'offline_utility' ||
    kind === 'gstr3b_working' ||
    kind === 'working_sheet' ||
    kind === 'gstr3b_return' ||
    kind === 'gstr3b_challan'
  )
    return <FileSpreadsheet size={16} />
  return <FileText size={16} />
}

export function Download() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [gates, setGates] = useState<PeriodGates | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The legacy's section picker, adapted: choose which artifacts go in the ZIP.
  const [picked, setPicked] = useState<Set<string>>(new Set())

  useEffect(() => {
    // Scoped to the client in the URL (ND-C1). Before this, every screen
    // listed every client's periods together and the rail asked you to pick
    // one out of the pile — which is the shape of a wrong-client mistake.
    void periods
      .list({ client_id: client.id })
      .then((b) => {
        setRows(b.data)
        // NOT `cur ?? …`: the effect re-runs when the client changes, and
        // keeping the previous selection would leave another client's period
        // on screen. Right numbers, wrong client is the mistake this whole
        // slice exists to make impossible.
        setSelected(b.data[0]?.id ?? null)
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.'),
      )
  }, [client.id])

  const load = useCallback(async () => {
    if (!selected) {
      setGates(null)
      return
    }
    try {
      setGates((await periods.gates(selected)).data)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The period could not be loaded.')
    }
  }, [selected])

  useEffect(() => {
    void load()
  }, [load])

  const fetchAndOpen = useCallback(
    async (artifactId: string) => {
      if (!selected) return
      setBusy(artifactId)
      setError(null)
      try {
        // The bytes, from ProGST itself; saved under the name it sent (D-58).
        saveFile(await periods.artifactFile(selected, artifactId), 'download')
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That download could not be prepared.')
      } finally {
        setBusy(null)
      }
    },
    [selected],
  )

  const artifacts = gates?.artifacts ?? []

  /**
   * Save a bundle. The browser is handed a blob rather than a URL because the
   * ZIP is built per request from bytes the service reads itself — there is no
   * stored object to presign.
   */
  const saveZip = useCallback(
    async (ids: string[], label: string) => {
      if (!selected) return
      setBusy(label)
      setError(null)
      try {
        const blob = await periods.artifactsZip(selected, ids)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        // The return by its name, as the rail and header print it (GSTR-3B, never GSTR3B).
        link.download = `${RETURN_NAMES[gates?.period.return_type ?? ''] ?? 'return'}-${gates?.period.period ?? ''}-artifacts.zip`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That bundle could not be built.')
      } finally {
        setBusy(null)
      }
    },
    [selected, gates],
  )

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <DownloadIcon size={20} /> Download
          </h1>
          <p className="gst-sub">
            Generated artifacts, each bound to the state that produced it — not a live render
          </p>
        </div>
        {artifacts.length > 0 && (
          <Button
            type="button"
            disabled={busy !== null}
            onClick={() => void saveZip([], 'all')}
            variant="secondary" size="sm"
          >
            <DownloadIcon size={15} />
            {busy === 'all' ? 'Building…' : 'Download All (ZIP)'}
          </Button>
        )}
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <div className="flex flex-wrap gap-6">
        <aside className="w-72 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="truncate">{returnName(row.return_type)}</span>
                    <span className="mt-0.5 block text-[11px]">{periodWords(row.period)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!gates ? (
            <p className="gst-sub">Select a period.</p>
          ) : artifacts.length === 0 ? (
            <div className="gst-empty">
              Nothing has been generated for this period yet. Artifacts are produced when the
              return moves through its lifecycle — prepare it first.
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {gates.client_name} — {returnName(gates.period.return_type)}{' '}
                {periodWords(gates.period.period)}
              </p>
              <p className="gst-meta mb-4">
                A download link is minted per click and expires shortly after.
              </p>

              <div className="gst-table-wrap">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">
                        <input
                          type="checkbox"
                          aria-label="Select all artifacts"
                          checked={picked.size === artifacts.length && artifacts.length > 0}
                          onChange={(e) =>
                            setPicked(
                              e.target.checked ? new Set(artifacts.map((a) => a.id)) : new Set(),
                            )
                          }
                        />
                      </th>
                      <th className="is-text">Artifact</th>
                      <th className="is-text">Version</th>
                      <th className="is-text">Status</th>
                      <th className="is-text">Generated from</th>
                      <th className="is-text">SHA-256</th>
                      <th className="is-text">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts.map((a, i) => (
                      <tr key={a.id} className={i % 2 === 1 ? 'is-a' : ''}>
                        <td className="is-text">
                          <input
                            type="checkbox"
                            aria-label={`Select ${KIND_LABEL[a.kind] ?? 'this file'}`}
                            checked={picked.has(a.id)}
                            onChange={(e) => {
                              const next = new Set(picked)
                              if (e.target.checked) next.add(a.id)
                              else next.delete(a.id)
                              setPicked(next)
                            }}
                          />
                        </td>
                        <td className="is-text is-sec">
                          <span className="inline-flex items-center gap-2">
                            <KindIcon kind={a.kind} />
                            {KIND_LABEL[a.kind] ?? 'A file of a kind ProGST has no name for'}
                          </span>
                          <small>{KIND_HINT[a.kind] ?? ''}</small>
                        </td>
                        <td className="is-text">v{a.version_no}</td>
                        <td className="is-text">
                          <span className={`gst-chip ${a.status === 'current' ? 'is-ok' : 'is-hold'}`}>
                            {artifactStatusWords(a, artifacts)}
                          </span>
                        </td>
                        <td className="is-text">
                          {STATE_LABELS[a.generated_from_state] ?? a.generated_from_state}
                        </td>
                        <td className="is-text">
                          <span className="gst-mono">{a.sha256.slice(0, 16)}…</span>
                        </td>
                        <td className="is-text">
                          <Button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void fetchAndOpen(a.id)}
                            variant={a.status === 'current' ? 'secondary' : 'outline'} size="sm"
                          >
                            <DownloadIcon size={14} />
                            {busy === a.id ? 'Preparing…' : 'Download'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {picked.size > 0 && (
                <Button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void saveZip([...picked], 'picked')}
                  variant="outline" size="sm" className="mt-3"
                >
                  <DownloadIcon size={14} />
                  {busy === 'picked'
                    ? 'Building…'
                    : `Download selected (${picked.size}) as ZIP`}
                </Button>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
